import React, { memo, useState, useCallback, useMemo, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  Drawer,
  Button,
  useMediaQuery,
  useTheme,
  Divider,
  Badge,
  Stack,
} from "@mui/material";
import MomentUtils from "@date-io/moment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Clear, FilterList, SearchOutlined } from "@mui/icons-material";
import { Search } from "react-feather";
import { isEmpty, omit } from "lodash";
import { LoadingButton } from "@mui/lab";
import { useSelector } from "react-redux";

import { TextFieldNumber } from "src/_modules/common";
import {
  useGetWarehousesByFilterQuery,
  useGetWarehousesQuery,
} from "src/_api/warehouseApi";
import { MapitShipCitiesAutocomplete } from "src/_modules/MapitShip/components";
import { MerchantAutocomplete } from "src/_modules/common/ServerAutocomplete";
import { ROLES } from "src/constants";

/**
 * Simple text input - no debouncing needed since we sync on submit
 */
const FilterTextField = memo(function FilterTextField({
  name,
  value,
  onChange,
  ...props
}) {
  const handleChange = useCallback(
    (e) => {
      onChange({ target: { name, value: e.target.value } });
    },
    [name, onChange],
  );

  return (
    <TextField
      {...props}
      name={name}
      value={value ?? ""}
      onChange={handleChange}
    />
  );
});

/**
 * Simple number input - no debouncing needed since we sync on submit
 */
const FilterNumberField = memo(function FilterNumberField({
  name,
  value,
  onChange,
  ...props
}) {
  const handleChange = useCallback(
    (e) => {
      onChange({ target: { name, value: e.target.value } });
    },
    [name, onChange],
  );

  return (
    <TextFieldNumber
      {...props}
      name={name}
      value={value ?? ""}
      onChange={handleChange}
    />
  );
});

/**
 * Date range picker component - memoized
 */
const DateRangePicker = memo(function DateRangePicker({
  fromDate,
  toDate,
  onFromChange,
  onToChange,
  onFromClear,
  onToClear,
  t,
}) {
  const handleFromChange = useCallback(
    (newValue) => {
      onFromChange(newValue?.startOf("day")?.toISOString());
    },
    [onFromChange],
  );

  const handleToChange = useCallback(
    (newValue) => {
      onToChange(newValue?.endOf("day")?.toISOString());
    },
    [onToChange],
  );

  return (
    <LocalizationProvider dateAdapter={MomentUtils}>
      <Grid container alignItems="center" spacing={1}>
        <Grid item xs={12} md={6}>
          <DatePicker
            disableMaskedInput
            inputFormat="DD/MM/YYYY"
            maxDate={toDate ? moment(toDate) : moment()}
            label={t("From")}
            value={fromDate ? moment(fromDate) : null}
            onChange={handleFromChange}
            renderInput={(inputParams) => {
              inputParams.InputProps = {
                ...inputParams.InputProps,
                startAdornment: fromDate ? (
                  <InputAdornment position="start">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        onFromClear();
                      }}
                      size="small"
                      color="primary"
                    >
                      <Clear sx={{ fontSize: "inherit" }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              };
              return (
                <TextField
                  sx={{ mr: 1 }}
                  fullWidth
                  size="small"
                  {...inputParams}
                />
              );
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <DatePicker
            disableMaskedInput
            inputFormat="DD/MM/YYYY"
            maxDate={moment()}
            minDate={fromDate ? moment(fromDate) : moment().subtract(1, "y")}
            label={t("To")}
            value={toDate ? moment(toDate) : null}
            onChange={handleToChange}
            renderInput={(inputParams) => {
              inputParams.InputProps = {
                ...inputParams.InputProps,
                startAdornment: toDate ? (
                  <InputAdornment position="start">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        onToClear();
                      }}
                      size="small"
                      color="primary"
                    >
                      <Clear sx={{ fontSize: "inherit" }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              };
              return <TextField fullWidth size="small" {...inputParams} />;
            }}
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
});

/**
 * Warehouse autocomplete - memoized
 */
const WarehouseAutocomplete = memo(function WarehouseAutocomplete({
  value,
  warehouses,
  isLoading,
  onChange,
  t,
}) {
  const selectedWarehouse = useMemo(
    () => warehouses?.docs?.find((wh) => wh.id === value) ?? null,
    [warehouses?.docs, value],
  );

  const handleChange = useCallback(
    (e, newValue) => {
      onChange({
        target: { name: "pickupLocation", value: newValue?.id ?? null },
      });
    },
    [onChange],
  );

  return (
    <Autocomplete
      value={selectedWarehouse}
      onChange={handleChange}
      options={warehouses?.docs ?? []}
      loading={isLoading}
      getOptionLabel={(option) => option.name}
      renderInput={(params) => (
        <TextField {...params} label={t("Pickup Location")} />
      )}
      fullWidth
      size="small"
      loadingText={t("loading...")}
    />
  );
});

/**
 * Payment method select - memoized
 */
const PaymentMethodSelect = memo(function PaymentMethodSelect({
  value,
  onChange,
  t,
}) {
  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      onChange({ target: { name: "paymentMethod", value: null } });
    },
    [onChange],
  );

  return (
    <TextField
      name="paymentMethod"
      label={t("Payment method")}
      value={value ?? ""}
      onChange={onChange}
      fullWidth
      variant="outlined"
      size="small"
      select
      InputProps={{
        startAdornment: value ? (
          <InputAdornment position="start">
            <IconButton onClick={handleClear} size="small" color="primary">
              <Clear sx={{ fontSize: "inherit" }} />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    >
      <MenuItem value="paid">{t("paid")}</MenuItem>
      <MenuItem value="cod">{t("cod")}</MenuItem>
    </TextField>
  );
});

/**
 * Integration type select - memoized
 */
const IntegrationTypeSelect = memo(function IntegrationTypeSelect({
  value,
  onChange,
  t,
}) {
  const handleClear = useCallback(
    (e) => {
      e.stopPropagation();
      onChange({ target: { name: "integrationType", value: null } });
    },
    [onChange],
  );

  return (
    <TextField
      fullWidth
      select
      name="integrationType"
      label={t("Integration Type")}
      value={value ?? ""}
      onChange={onChange}
      variant="outlined"
      size="small"
      InputProps={{
        startAdornment: value ? (
          <InputAdornment position="start">
            <IconButton onClick={handleClear} size="small" color="primary">
              <Clear sx={{ fontSize: "inherit" }} />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    >
      <MenuItem value="aramex">{t("Aramex")}</MenuItem>
      <MenuItem value="oto">{t("OTO")}</MenuItem>
      <MenuItem value="secom">{t("SMSA")}</MenuItem>
      <MenuItem value="Redbox">{t("RedBox")}</MenuItem>
    </TextField>
  );
});

/**
 * Filter content component - vertical stacked layout for drawer
 */
const FilterContent = memo(function FilterContent({
  params,
  onChange,
  profile,
  warehouses,
  warehousesLoading,
  warehousesFetching,
  status,
}) {
  const { t } = useTranslation();

  // Memoized callbacks for date picker
  const handleFromDateChange = useCallback(
    (value) => {
      onChange({ target: { name: "fromOrderDate", value } });
    },
    [onChange],
  );

  const handleToDateChange = useCallback(
    (value) => {
      onChange({ target: { name: "toOrderDate", value } });
    },
    [onChange],
  );

  const handleFromDateClear = useCallback(() => {
    onChange({ target: { name: "fromOrderDate", value: null } });
  }, [onChange]);

  const handleToDateClear = useCallback(() => {
    onChange({ target: { name: "toOrderDate", value: null } });
  }, [onChange]);

  const isAdmin = profile?.role === ROLES.admin;

  return (
    <Stack spacing={2}>
      {/* Date Range */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {!status ? t("Date") : t(`orderDateRangeLabel.${status}`)}
        </Typography>
        <DateRangePicker
          fromDate={params?.fromOrderDate}
          toDate={params?.toOrderDate}
          onFromChange={handleFromDateChange}
          onToChange={handleToDateChange}
          onFromClear={handleFromDateClear}
          onToClear={handleToDateClear}
          t={t}
        />
      </Box>

      {/* Order Cost */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t("Order cost")}
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <FilterNumberField
              decimal={true}
              label={t("Min")}
              name="minOrderGrandTotal"
              value={params?.minOrderGrandTotal}
              onChange={onChange}
              variant="outlined"
              size="small"
              max={999999}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <FilterNumberField
              decimal={true}
              label={t("Max")}
              name="maxOrderGrandTotal"
              value={params?.maxOrderGrandTotal}
              onChange={onChange}
              variant="outlined"
              size="small"
              max={999999}
              fullWidth
            />
          </Grid>
        </Grid>
      </Box>

      {/* Merchant (Admin only) */}
      {isAdmin && (
        <MerchantAutocomplete
          deliveryMethod="MAPIT_SHIP"
          name="merchant"
          value={params?.merchant ?? null}
          onChange={onChange}
        />
      )}

      {/* Warehouse/Pickup Location */}
      <WarehouseAutocomplete
        value={params?.pickupLocation}
        warehouses={warehouses}
        isLoading={warehousesLoading || warehousesFetching}
        onChange={onChange}
        t={t}
      />

      {/* City */}
      <MapitShipCitiesAutocomplete
        name="destinationCity"
        value={params?.destinationCity ?? null}
        onChange={onChange}
        label={t("City")}
        size="small"
        variant="outlined"
      />

      {/* Payment Method */}
      <PaymentMethodSelect
        value={params?.paymentMethod}
        onChange={onChange}
        t={t}
      />

      {/* Customer Name */}
      <FilterTextField
        fullWidth
        name="customerName"
        value={params?.customerName}
        onChange={onChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        placeholder={t("Customer Name")}
        variant="outlined"
        size="small"
      />

      {/* Customer Address */}
      <FilterTextField
        fullWidth
        name="customerAddress"
        value={params?.customerAddress}
        onChange={onChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        placeholder={t("Customer Address")}
        variant="outlined"
        size="small"
      />

      {/* Order Number */}
      <FilterTextField
        fullWidth
        name="searchKey"
        value={params?.searchKey}
        onChange={onChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        placeholder={t("Order No.")}
        variant="outlined"
        size="small"
      />

      {/* Reference Number */}
      <FilterTextField
        fullWidth
        name="merchantOrderNumber"
        value={params?.merchantOrderNumber}
        onChange={onChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        placeholder={t("Refrence Number")}
        variant="outlined"
        size="small"
      />

      {/* Shipment Number */}
      <FilterTextField
        fullWidth
        name="shipmentNumber"
        value={params?.shipmentNumber}
        onChange={onChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        placeholder={t("Shipment Number")}
        variant="outlined"
        size="small"
      />

      {/* Integration Type (Admin only) */}
      {isAdmin && (
        <IntegrationTypeSelect
          value={params?.integrationType}
          onChange={onChange}
          t={t}
        />
      )}
    </Stack>
  );
});

/**
 * OrdersFilter - Uses a filter icon button + side Drawer
 *
 * Key optimizations:
 * 1. Memoized sub-components to prevent unnecessary re-renders
 * 2. Stable callback references with useCallback
 * 3. Separate filter content component for better memoization
 * 4. Snapshot-based cancel to revert changes on close
 */
function OrdersFilter({
  params,
  setParams,
  onSubmit,
  onChange,
  isLoading,
  status,
}) {
  const { t } = useTranslation();
  const { profile } = useSelector((state) => state.entities.profile);
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  const isAdmin = profile?.role === ROLES.admin;

  // Warehouse queries
  const skipMerchantQuery = useMemo(() => isAdmin, [isAdmin]);

  const { data: merchantWarehouses, isLoading: merchantWarehousesLoading, isFetching: merchantWarehouseFetching } =
    useGetWarehousesQuery({ limit: 100, page: 1 }, { skip: skipMerchantQuery });

  const { data: adminMerchantWarehouses, isLoading: adminMerchantWarehousesLoading, isFetching: adminWarehouseFetching } =
    useGetWarehousesByFilterQuery(
      { limit: 100, page: 1, merchant: params?.merchant },
      { skip: !isAdmin || !params?.merchant },
    );

  const warehouses = useMemo(() => {
    return isAdmin ? adminMerchantWarehouses : merchantWarehouses;
  }, [adminMerchantWarehouses, merchantWarehouses, isAdmin]);

  const warehousesLoading =
    merchantWarehousesLoading || adminMerchantWarehousesLoading;
  const warehousesFetching =
    adminWarehouseFetching || merchantWarehouseFetching;

  // Clear warehouse when merchant is removed (admin only)
  useEffect(() => {
    if (isAdmin && !params?.merchant && params?.pickupLocation) {
      onChange({ target: { name: "pickupLocation", value: null } });
    }
  }, [params?.merchant, params?.pickupLocation, isAdmin, onChange]);

  // Drawer state
  const [open, setOpen] = useState(false);
  const [snapshot, setSnapshot] = useState({});

  const handleOpenDrawer = useCallback(() => {
    setSnapshot(params);
    setOpen(true);
  }, [params]);

  const handleCloseDrawer = useCallback(() => {
    setParams(snapshot);
    setOpen(false);
  }, [setParams, snapshot]);

  const handleSubmitAndClose = useCallback(() => {
    onSubmit();
    setOpen(false);
  }, [onSubmit]);

  // Reset all filter values (keep page, limit, status) and submit
  const handleResetFilters = useCallback(() => {
    const resetParams = { page: 1, limit: params?.limit, status: params?.status };
    setParams(resetParams);
    setOpen(false);
    // Defer onSubmit so state and ref are updated before applyFilters reads them
    setTimeout(() => {
      onSubmit();
    }, 0);
  }, [params?.limit, params?.status, setParams, onSubmit]);

  // Check for active filters
  const hasActiveFilters = useMemo(() => {
    return Object.values(omit(params, ["page", "limit", "status"])).some(
      (value) => !isEmpty(value),
    );
  }, [params]);

  const drawerWidth = isMdDown ? "100%" : 400;

  return (
    <>
      <Badge
        invisible={!hasActiveFilters}
        color="error"
        badgeContent={
          <Clear
            sx={{ fontSize: 16, fontWeight: "bold", cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              handleResetFilters();
            }}
          />
        }
        sx={{
          "& .MuiBadge-badge": {
            width: 20,
            height: 20,
            minWidth: 20,
            p: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        }}
      >
        <Button
          onClick={handleOpenDrawer}
          color="primary"
          variant="outlined"
          startIcon={<FilterList />}
          sx={{ textTransform: "none" }}
        >
          {t("Filter")}
        </Button>
      </Badge>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: drawerWidth,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Drawer Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            pt: 10,
          }}
        >
          <Typography variant="h6">{t("Filter")}</Typography>
          <IconButton
            onClick={handleCloseDrawer}
            size="small"
            color="primary"
          >
            <Clear />
          </IconButton>
        </Box>
        <Divider />

        {/* Drawer Body */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }}>
          <FilterContent
            params={params}
            onChange={onChange}
            profile={profile}
            warehouses={warehouses}
            warehousesLoading={warehousesLoading}
            warehousesFetching={warehousesFetching}
            status={status}
          />
        </Box>

        {/* Drawer Footer */}
        <Divider />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            gap: 1,
          }}
        >
          <Button
            onClick={handleResetFilters}
            variant="outlined"
            color="primary"
            sx={{ textTransform: "none", flex: 1 }}
          >
            {t("Reset")}
          </Button>
          <LoadingButton
            onClick={handleSubmitAndClose}
            variant="contained"
            color="primary"
            startIcon={<SearchOutlined />}
            sx={{ textTransform: "none", flex: 1 }}
            loading={isLoading}
          >
            {t("Search")}
          </LoadingButton>
        </Box>
      </Drawer>
    </>
  );
}

export default memo(OrdersFilter);
